/**
 * Telecom v2 — redesigned "Cyber Resilience & Functional Adequacy" panel.
 *
 * Replaces the prior dial + traffic-light bars + separate
 * NIST commentary panel for telecom_v2 only. Executive-friendly:
 *   • header with overall band + one-line summary
 *   • six color-coded horizontal bars (no numbers / percentages)
 *   • commentary paragraph per function tied to its band
 *   • deterministic Priority Focus Areas block
 *
 * Data source: `calculate_nist_function_scores` RPC (live) and the
 * findings already loaded by ReviewerConsole. No scoring logic changes.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, AlertTriangle } from "lucide-react";
import { nistFunctionNarrative } from "@/reports/telecom/domainNarratives";
import {
  BAND_COLORS,
  bandFromAverage,
  buildFunctionVisualRows,
  buildPriorityFocusAreas,
  overallSummaryLine,
  type FindingRow,
  type FunctionScoreRow,
  type MaturityBand,
} from "@/reports/telecom/cyberResilienceSummary";

const sb: any = supabase as any;

type Props = {
  assessmentId: string;
  /** Bump to refresh after a reviewer rating save. */
  refreshKey?: number;
  /** Used to phrase the executive header summary line. */
  orgName?: string | null;
  /** Optional reviewer override for overall band; when provided, replaces the calculated band in the header only. */
  overrideBand?: MaturityBand | null;
  /** Findings list used to enrich Priority Focus Areas. */
  findings?: FindingRow[];
};

export function CyberResilienceFunctionalAdequacy({
  assessmentId,
  refreshKey,
  orgName,
  overrideBand,
  findings = [],
}: Props) {
  const [dbScores, setDbScores] = useState<FunctionScoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await sb.rpc("calculate_nist_function_scores", {
        p_assessment_id: assessmentId,
      });
      if (rpcErr) throw rpcErr;
      setDbScores(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error("[CyberResilienceFunctionalAdequacy] load error:", e);
      setError(e?.message ?? "Failed to load function scores");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const visualRows = useMemo(() => buildFunctionVisualRows(dbScores), [dbScores]);

  // Overall band: mean across rated functions (deterministic, not exposed numerically).
  const calculatedOverall: MaturityBand | "Unrated" = useMemo(() => {
    const ratedAverages = dbScores
      .map((s) => Number(s.average_score))
      .filter((n) => Number.isFinite(n));
    if (ratedAverages.length === 0) return "Unrated";
    const avg = ratedAverages.reduce((a, b) => a + b, 0) / ratedAverages.length;
    return bandFromAverage(avg, ratedAverages.length) as MaturityBand;
  }, [dbScores]);

  const overallBand: MaturityBand | "Unrated" = overrideBand ?? calculatedOverall;
  const overallStyle = BAND_COLORS[overallBand];

  const priorityAreas = useMemo(
    () => buildPriorityFocusAreas(visualRows, findings),
    [visualRows, findings],
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Calculating cyber resilience…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-4 w-4 text-primary" />
          Cyber Resilience &amp; Functional Adequacy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Executive header */}
        <div
          className="rounded-lg border p-4"
          style={{ backgroundColor: overallStyle.bg, borderColor: overallStyle.hex }}
        >
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium" style={{ color: overallStyle.text }}>
              Cyber Resilience:
            </span>
            <span
              className="text-lg font-bold tracking-tight"
              style={{ color: overallStyle.hex }}
            >
              {overallBand === "Unrated" ? "Not yet rated" : overallBand}
            </span>
          </div>
          <p className="mt-1 text-sm" style={{ color: overallStyle.text }}>
            {overallSummaryLine(overallBand, orgName)}
          </p>
        </div>

        {/* Visual bars */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">
            NIST CSF 2.0 — Functional Adequacy
          </h4>
          <div className="space-y-2">
            {visualRows.map((row) => {
              const style = BAND_COLORS[row.band];
              return (
                <div key={row.fn} className="flex items-center gap-3">
                  <div className="w-20 shrink-0 text-sm font-medium text-foreground">{row.fn}</div>
                  <div className="flex-1 h-6 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(row.fill, 6)}%`,
                        backgroundColor: style.hex,
                        opacity: row.band === "Unrated" ? 0.35 : 1,
                      }}
                    />
                  </div>
                  <div
                    className="w-24 shrink-0 text-right text-xs font-semibold"
                    style={{ color: style.hex }}
                  >
                    {row.band === "Unrated" ? "Not rated" : row.band}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
            {(["Minimal", "Adequate", "Strong", "Extensive"] as const).map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: BAND_COLORS[b].hex }}
                />
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Commentary per function */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">Function Commentary</h4>
          <div className="space-y-3">
            {visualRows.map((row) => {
              const style = BAND_COLORS[row.band];
              const dbRow = dbScores.find((s) => String(s.function_name) === row.fn);
              const text = nistFunctionNarrative(
                row.fn,
                Number(dbRow?.average_score ?? 0),
                Number(dbRow?.controls_evaluated ?? 0),
              );
              return (
                <div key={row.fn} className="rounded-md border bg-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-foreground">{row.fn}</span>
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      style={{ backgroundColor: style.bg, color: style.text }}
                    >
                      {row.band === "Unrated" ? "Not rated" : row.band}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">{text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Priority Focus Areas */}
        {priorityAreas.length > 0 && (
          <div
            className="rounded-lg border p-4"
            style={{ backgroundColor: "#FEF3C7", borderColor: "#F59E0B" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" style={{ color: "#78350F" }} />
              <h4 className="text-sm font-semibold" style={{ color: "#78350F" }}>
                Priority Focus Areas
              </h4>
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: "#78350F" }}>
              {priorityAreas.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
